import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/utils/apiClient';
import type { PractitionerClient, CreateClientRequest, UpdateClientRequest } from '@/types/practitioner';

export const usePractitionerClients = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [clients, setClients] = useState<PractitionerClient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchClients = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      console.log('👥 Fetching clients for practitioner:', user.id);

      // Use apiClient with JSON-encoded filters (backend database_adapter format)
      const filters = JSON.stringify([
        { type: 'eq', column: 'practitioner_id', value: user.id },
        { type: 'eq', column: 'is_active', value: true }
      ]);
      const order = JSON.stringify([{ column: 'created_at', ascending: false }]);

      const data = await apiClient.get<PractitionerClient[]>(
        `/db/practitioner_clients?filters=${encodeURIComponent(filters)}&order=${encodeURIComponent(order)}`,
        { includeAuth: true }
      );

      console.log('👥 Clients query result:', data);
      console.log('👥 Clients count found:', data?.length || 0);

      setClients((data || []).map(client => ({
        ...client,
        preferences: (client.preferences as Record<string, any>) || {},
        biography: client.biography || '',
      })));
    } catch (err: any) {
      console.error('Error fetching clients:', err);
      setError(err.message || 'Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  const createClient = async (request: CreateClientRequest): Promise<PractitionerClient | null> => {
    if (!user) {
      console.error('❌ CREATE CLIENT: No authenticated user');
      toast({
        title: "Authentication Error",
        description: "Please log in to add clients.",
        variant: "destructive",
      });
      return null;
    }

    try {
      // Construct client_name for backward compatibility
      const clientName = `${request.first_name}${request.last_name ? ' ' + request.last_name : ''}`.trim();

      console.log('🔍 CREATE CLIENT: Attempting to create client with data:', {
        ...request,
        client_name: clientName,
        practitioner_id: user.id,
      });

      // Count existing clients first to debug
      const filters = JSON.stringify([
        { type: 'eq', column: 'practitioner_id', value: user.id },
        { type: 'eq', column: 'is_active', value: true }
      ]);
      const existingClients = await apiClient.get<PractitionerClient[]>(
        `/db/practitioner_clients?filters=${encodeURIComponent(filters)}&select=id`,
        { includeAuth: true }
      );

      console.log('📊 CREATE CLIENT: Current active client count:', existingClients?.length || 0);

      // Create new client using apiClient
      const data = await apiClient.post<PractitionerClient>(
        '/db/practitioner_clients',
        {
          ...request,
          client_name: clientName, // Include for legacy compatibility
          practitioner_id: user.id,
          is_active: true, // Explicitly set to ensure RLS policy compliance
        },
        { includeAuth: true }
      );

      console.log('✅ CREATE CLIENT: Client created successfully:', data);

      await fetchClients(); // Refresh the list
      toast({
        title: "Client Added",
        description: `${data.first_name}${data.last_name ? ' ' + data.last_name : ''} has been added to your client list.`,
      });

      return {
        ...data,
        preferences: (data.preferences as Record<string, any>) || {},
        biography: data.biography || '',
      };
    } catch (err: any) {
      console.error('❌ CREATE CLIENT: Caught error:', err);

      // Provide specific error messages based on error type
      let errorMessage = err.message || "Failed to add client. Please try again.";
      if (errorMessage.includes('row-level security') || errorMessage.includes('permission')) {
        errorMessage = "Permission denied. Please check your account permissions.";
      } else if (errorMessage.includes('limit exceeded')) {
        errorMessage = "You have reached the maximum number of clients allowed.";
      } else if (errorMessage.includes('unique constraint') || errorMessage.includes('already exists')) {
        errorMessage = "A client with this information already exists.";
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      return null;
    }
  };

  const updateClient = async (id: string, request: UpdateClientRequest): Promise<PractitionerClient | null> => {
    if (!user) {
      console.error('❌ UPDATE CLIENT: No authenticated user');
      toast({
        title: "Authentication Error",
        description: "Please log in to update clients.",
        variant: "destructive",
      });
      return null;
    }

    try {
      // If first_name or last_name are being updated, also update client_name for compatibility
      const updatedRequest = { ...request };
      if ('first_name' in request || 'last_name' in request) {
        // Fetch current data to construct full name
        const filters = JSON.stringify([{ type: 'eq', column: 'id', value: id }]);
        const currentClients = await apiClient.get<PractitionerClient[]>(
          `/db/practitioner_clients?filters=${encodeURIComponent(filters)}&select=first_name,last_name`,
          { includeAuth: true }
        );

        const currentClient = currentClients?.[0];
        const firstName = request.first_name ?? currentClient?.first_name ?? '';
        const lastName = request.last_name ?? currentClient?.last_name ?? '';
        updatedRequest.client_name = `${firstName}${lastName ? ' ' + lastName : ''}`.trim();
      }

      // Update client using apiClient
      const filters = JSON.stringify([
        { type: 'eq', column: 'id', value: id },
        { type: 'eq', column: 'practitioner_id', value: user.id }
      ]);
      const data = await apiClient.patch<PractitionerClient[]>(
        `/db/practitioner_clients?filters=${encodeURIComponent(filters)}`,
        updatedRequest,
        { includeAuth: true }
      );

      if (!data || data.length === 0) {
        throw new Error('Client not found or update failed');
      }

      await fetchClients(); // Refresh the list
      toast({
        title: "Client Updated",
        description: "Client information has been updated.",
      });

      return {
        ...data[0],
        preferences: (data[0].preferences as Record<string, any>) || {},
        biography: data[0].biography || '',
      };
    } catch (err: any) {
      console.error('Error updating client:', err);
      toast({
        title: "Error",
        description: err.message || "Failed to update client. Please try again.",
        variant: "destructive",
      });
      return null;
    }
  };

  const deleteClient = async (id: string): Promise<boolean> => {
    if (!user) {
      console.error('❌ DELETE CLIENT: No authenticated user');
      toast({
        title: "Authentication Error",
        description: "Please log in to remove clients.",
        variant: "destructive",
      });
      return false;
    }

    try {
      console.log('🗑️ DELETE CLIENT: Attempting to deactivate client:', id);

      // Soft delete - set is_active to false using apiClient
      const filters = JSON.stringify([
        { type: 'eq', column: 'id', value: id },
        { type: 'eq', column: 'practitioner_id', value: user.id }
      ]);
      const data = await apiClient.patch<PractitionerClient[]>(
        `/db/practitioner_clients?filters=${encodeURIComponent(filters)}`,
        { is_active: false },
        { includeAuth: true }
      );

      console.log('🗑️ DELETE CLIENT: Update result:', data);

      if (!data || data.length === 0) {
        console.log('🗑️ DELETE CLIENT: Client not found or already inactive');
        toast({
          title: "Client Not Found",
          description: "Client not found or already removed from your list.",
          variant: "destructive",
        });
        return false;
      }

      await fetchClients(); // Refresh the list
      toast({
        title: "Client Removed",
        description: "Client has been removed from your list.",
      });

      return true;
    } catch (err: any) {
      console.error('🗑️ DELETE CLIENT: Caught error:', err);

      let errorMessage = err.message || "Failed to remove client. Please try again.";
      if (errorMessage.includes('401') || errorMessage.includes('Not authenticated')) {
        errorMessage = "Please log in to remove clients.";
      } else if (errorMessage.includes('not found') || errorMessage.includes('404')) {
        errorMessage = "Client not found. It may have already been removed.";
      } else if (errorMessage.includes('Access denied') || errorMessage.includes('403')) {
        errorMessage = "You don't have permission to remove this client.";
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    }
  };

  const getClient = (id: string): PractitionerClient | undefined => {
    return clients.find(client => client.id === id);
  };

  /**
   * Save client's questionnaire preferences for future sessions.
   * Preferences are stored per category to prevent data pollution.
   */
  const saveClientQuestionnairePreferences = async (
    clientId: string,
    categoryId: string,
    questionnaireData: Record<string, any>
  ): Promise<boolean> => {
    if (!user) {
      console.error('❌ SAVE PREFERENCES: No authenticated user');
      return false;
    }

    try {
      console.log('💾 Saving client questionnaire preferences:', { clientId, categoryId, questionnaireData });

      // Extract fields that rarely change (personal details, not session-specific)
      const preferencesToStore = {
        // Personal details that rarely change
        age: questionnaireData.age,
        occupation: questionnaireData.occupation,
        location: questionnaireData.location,
        hobbies: questionnaireData.hobbies,
        stressors: questionnaireData.stressors,
        goals: questionnaireData.goals,
        relationship: questionnaireData.relationship,

        // Session preferences that might be reused
        preferredStyle: questionnaireData.preferredStyle,
        environment: questionnaireData.environment,
        sessionLength: questionnaireData.sessionLength,
        backgroundSounds: questionnaireData.backgroundSounds,

        // Voice preferences
        voiceGender: questionnaireData.voiceGender,
        voiceType: questionnaireData.voiceType,
        specific_voice_id: questionnaireData.specific_voice_id,

        // Last updated timestamp
        lastUpdated: new Date().toISOString(),
      };

      // Fetch current client preferences using apiClient
      const getFilters = JSON.stringify([{ type: 'eq', column: 'id', value: clientId }]);
      const currentClients = await apiClient.get<PractitionerClient[]>(
        `/db/practitioner_clients?filters=${encodeURIComponent(getFilters)}&select=preferences`,
        { includeAuth: true }
      );

      const currentClient = currentClients?.[0];
      const existingPreferences = (currentClient?.preferences as Record<string, any>) || {};

      // Store preferences per category to prevent overwriting
      const mergedPreferences = {
        ...existingPreferences,
        questionnaire: {
          ...(existingPreferences.questionnaire || {}),
          [categoryId]: preferencesToStore,
        },
      };

      // Update preferences using apiClient
      const updateFilters = JSON.stringify([
        { type: 'eq', column: 'id', value: clientId },
        { type: 'eq', column: 'practitioner_id', value: user.id }
      ]);
      await apiClient.patch(
        `/db/practitioner_clients?filters=${encodeURIComponent(updateFilters)}`,
        { preferences: mergedPreferences },
        { includeAuth: true }
      );

      console.log(`✅ SAVE PREFERENCES: Successfully saved preferences for ${categoryId}`);

      // Update local state
      await fetchClients();

      return true;
    } catch (err: any) {
      console.error('❌ SAVE PREFERENCES: Caught error:', err);
      return false;
    }
  };

  /**
   * Retrieve client's stored questionnaire preferences for pre-filling forms.
   * Returns category-specific preferences to prevent data pollution.
   */
  const getClientQuestionnairePreferences = (
    clientId: string,
    categoryId: string
  ): Record<string, any> | null => {
    const client = clients.find(c => c.id === clientId);
    if (!client || !client.preferences) {
      return null;
    }

    const preferences = client.preferences as Record<string, any>;
    const questionnairePrefs = preferences.questionnaire;

    if (!questionnairePrefs || typeof questionnairePrefs !== 'object') {
      return null;
    }

    // Return category-specific preferences
    const categoryPrefs = questionnairePrefs[categoryId];

    if (!categoryPrefs || typeof categoryPrefs !== 'object') {
      console.log(`📋 No preferences found for category: ${categoryId}`);
      return null;
    }

    console.log(`📋 Retrieved preferences for ${categoryId}:`, categoryPrefs);
    return categoryPrefs;
  };

  useEffect(() => {
    fetchClients();
  }, [user]);

  return {
    clients,
    loading,
    error,
    fetchClients,
    createClient,
    updateClient,
    deleteClient,
    getClient,
    saveClientQuestionnairePreferences,
    getClientQuestionnairePreferences,
  };
};
